import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { api } from "@/lib/api";
import useRedirectFallback from "@/lib/useRedirectFallback";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import ContactForm from "@/components/ContactForm";
import EditOnSiteLink from "@/components/EditOnSiteLink";

export default function Page() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  useRedirectFallback(Boolean(error));

  useEffect(() => {
    setPage(null);
    setError("");
    api
      .get(`/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setError("Page not found."));
  }, [slug]);

  const seoSchema = useMemo(() => {
    if (!page) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbSchema([
          { name: "Home", url: `${window.location.origin}/` },
          { name: page.title, url: `${window.location.origin}/page/${page.slug}` },
        ]),
      ],
    };
  }, [page]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
        <div className="brut bg-white p-8" data-testid="page-not-found">
          <div className="font-display text-4xl mb-2">404</div>
          <p className="mb-4">{error}</p>
          <Link
            to="/"
            className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press"
          >
            Back home
          </Link>
        </div>
      </div>
    );

  if (!page)
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 font-mono uppercase text-xs tracking-widest opacity-70">
        Loading...
      </div>
    );

  return (
    <div data-testid={`page-${page.slug}`}>
      <Seo
        title={page.title}
        description={page.meta_description || page.title}
        path={`/page/${page.slug}`}
        type="article"
        schema={seoSchema}
      />

      <section className="border-b-2 border-black">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="font-mono uppercase text-[11px] tracking-widest opacity-60 mb-2">
            <Link to="/" className="hover:pcs-text-pink">
              Home
            </Link>{" "}
            / {page.title}
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-none">
            {page.title}
          </h1>
          <div className="mt-4">
            <EditOnSiteLink type="page" slug={page.slug} />
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="prose-pcs">
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{page.content_md}</ReactMarkdown>
        </div>

        {page.slug === "contact" && (
          <div className="mt-10">
            <ContactForm />
          </div>
        )}
      </article>
    </div>
  );
}
